/**
 * Кастомный GraphQL fetcher с поддержкой таймаутов и повторных попыток
 */

import type { RetryPolicy } from '@/core/request';
import {
    AbortedError,
    calculateRetryDelay,
    createErrorFromResponse,
    EmptyResponseError,
    isRetryableError,
    JSONParseError,
    NetworkError,
    sleep,
    TimeoutError
} from '@/core/request';

export const createGraphqlFetcher = (url: string, defaultTimeout = 30000) => {
    return async (body: any, options?: { timeout?: number; retries?: number; retryPolicy?: RetryPolicy }) => {
        const timeout = options?.timeout ?? defaultTimeout
        
        let maxRetries: number
        let retryPolicy: RetryPolicy | undefined
        
        if (options?.retryPolicy) {
            retryPolicy = options.retryPolicy
            maxRetries = retryPolicy.maxRetries
        } else if (options?.retries !== undefined) {
            maxRetries = options.retries
        } else {
            maxRetries = 0
        }
        
        const attempts = maxRetries + 1
        let lastError: any
        
        for (let attempt = 0; attempt < attempts; attempt++) {
            if (attempt > 0) {
                const delay = retryPolicy 
                    ? calculateRetryDelay(
                        attempt - 1,
                        retryPolicy.strategy,
                        retryPolicy.baseDelay,
                        retryPolicy.maxDelay
                    )
                    : 0
                
                if (delay > 0) {
                    console.log(`Retry attempt ${attempt}/${maxRetries}, waiting ${delay}ms...`)
                    await sleep(delay)
                }
            }
            
            const controller = new AbortController()
            const timeoutId = setTimeout(() => {
                controller.abort(new TimeoutError(timeout))
            }, timeout)

            try {
                const res = await fetch(url, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    method: 'POST',
                    body: JSON.stringify(body),
                    signal: controller.signal,
                })

                clearTimeout(timeoutId)

                if (!res.ok) {
                    const text = await res.text()
                    throw createErrorFromResponse(res.status, res.statusText, text)
                }

                const text = await res.text()
                
                if (!text || text.trim().length === 0) {
                    throw new EmptyResponseError('Server returned empty body')
                }
                
                let json
                try {
                    json = JSON.parse(text)
                } catch (e) {
                    throw new JSONParseError(text, e)
                }
                
                if (!json || (typeof json === 'object' && Object.keys(json).length === 0)) {
                    throw new EmptyResponseError('Server returned empty JSON object: {}')
                }
                
                return json
            } catch (error: any) {
                clearTimeout(timeoutId)
                
                if (error.name === 'AbortError') {
                    const reason = controller.signal.reason
                    if (reason instanceof TimeoutError) {
                        lastError = reason
                    } else {
                        lastError = new AbortedError(reason?.message)
                    }
                } else if (error.name === 'TypeError' && error.message?.includes('fetch')) {
                    lastError = new NetworkError(error.message, error)
                } else {
                    lastError = error
                }
                
                if (attempt === attempts - 1 || !isRetryableError(lastError)) {
                    throw lastError
                }
                
                console.warn(
                    `GraphQL request failed (attempt ${attempt + 1}/${attempts}):`,
                    lastError.message,
                    lastError.category ? `[${lastError.category}]` : ''
                )
            }
        }
        
        throw lastError || new Error('Unknown error')
    }
}

